import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import API from "../services/api";
import logo from "../assets/logo.png";
import "./EmployeeAgreement.css";
import "react-toastify/dist/ReactToastify.css";

const SIGN_PAD_WIDTH = 760;
const SIGN_PAD_HEIGHT = 220;

const fmtDateTime = (value) => {
  if (!value) return "Pending";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Pending";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const EmployeeAgreement = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSignModal, setShowSignModal] = useState(false);
  const [signName, setSignName] = useState("");
  const [signatureError, setSignatureError] = useState("");
  const canvasRef = useRef(null);
  const padRef = useRef({ drawing: false, moved: false, lastX: 0, lastY: 0 });

  const fetchAgreement = async () => {
    const response = await API.get("/employee/agreement");
    const payload = response?.data || null;
    setData(payload);
    setSignName(
      String(
        payload?.agreement?.signName ||
          payload?.employee?.fullName ||
          ""
      ).trim()
    );
    return payload;
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await fetchAgreement();
      } catch (error) {
        toast.error(error?.response?.data?.message || "Failed to load employee agreement");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!showSignModal) return undefined;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 3;
    ctx.strokeStyle = "#0f4c3a";
    padRef.current = { drawing: false, moved: false, lastX: 0, lastY: 0 };
    return undefined;
  }, [showSignModal]);

  const agreement = data?.agreement || {};
  const company = data?.company || {};
  const employee = data?.employee || {};
  const sections = Array.isArray(data?.sections) ? data.sections : [];
  const isAccepted = !!agreement.accepted;

  const initials = useMemo(() => {
    const parts = String(employee.fullName || "")
      .split(" ")
      .map((part) => part.trim())
      .filter(Boolean)
      .slice(0, 2);
    return parts.map((part) => part.charAt(0).toUpperCase()).join("") || "LR";
  }, [employee.fullName]);

  const getPoint = (event) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const clientX = "touches" in event ? event.touches[0]?.clientX : event.clientX;
    const clientY = "touches" in event ? event.touches[0]?.clientY : event.clientY;
    return {
      x: ((clientX - rect.left) * canvas.width) / rect.width,
      y: ((clientY - rect.top) * canvas.height) / rect.height,
    };
  };

  const startSignature = (event) => {
    if (event.cancelable) event.preventDefault();
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    const point = getPoint(event);
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
    padRef.current = { drawing: true, moved: false, lastX: point.x, lastY: point.y };
    setSignatureError("");
  };

  const moveSignature = (event) => {
    if (event.cancelable) event.preventDefault();
    if (!padRef.current.drawing) return;
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    const point = getPoint(event);
    ctx.beginPath();
    ctx.moveTo(padRef.current.lastX, padRef.current.lastY);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    padRef.current = { drawing: true, moved: true, lastX: point.x, lastY: point.y };
  };

  const endSignature = () => {
    padRef.current = { ...padRef.current, drawing: false };
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    padRef.current = { drawing: false, moved: false, lastX: 0, lastY: 0 };
    setSignatureError("");
  };

  const handleAccept = async () => {
    const safeName = String(signName || "").trim();
    if (!safeName) {
      setSignatureError("Signer name is required.");
      return;
    }
    if (!canvasRef.current || !padRef.current.moved) {
      setSignatureError("Please draw your digital signature before accepting.");
      return;
    }

    try {
      setSaving(true);
      const response = await API.post("/employee/agreement/accept", {
        signName: safeName,
        signatureDataUrl: canvasRef.current.toDataURL("image/png"),
      });
      setData(response?.data || null);
      setShowSignModal(false);
      setSignatureError("");
      toast.success("Agreement accepted and signature saved");
    } catch (error) {
      setSignatureError(error?.response?.data?.message || "Failed to save agreement");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="employee-agreement-loading">
        <Spinner animation="border" variant="success" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="employee-agreement-page">
        <Alert variant="danger">Employee agreement load nahi ho paya.</Alert>
        <ToastContainer position="top-right" autoClose={2500} />
      </div>
    );
  }

  return (
    <div className="employee-agreement-page">
      <section className="ea-hero">
        <div>
          <span className="ea-eyebrow">Digital Onboarding</span>
          <h2>Employee Agreement</h2>
          <p>
            Agreement ko dhyan se padhiye. Accept karne ke baad aapki digital signature aur timestamp
            permanently save ho jayega.
          </p>
          <div className="ea-hero-actions">
            <Badge bg={isAccepted ? "success" : "warning"} text={isAccepted ? "light" : "dark"}>
              {isAccepted ? "Accepted" : "Pending Acceptance"}
            </Badge>
            <span className="ea-meta-chip">Version {agreement.version || 1}</span>
          </div>
        </div>
        <div className="ea-hero-card">
          <div className="ea-initials">{initials}</div>
          <div>
            <strong>{employee.fullName || "Employee"}</strong>
            <div>{employee.departmentName || "Sales / Field Role"}</div>
            <div>{employee.mobile || "Mobile not available"}</div>
          </div>
        </div>
      </section>

      <Row className="g-4">
        <Col xl={8}>
          <section className="ea-paper">
            <div className="ea-watermark">LOTUS</div>
            <header className="ea-header">
              <div className="ea-brand">
                <img src={logo} alt="Lotus Rice" />
                <div>
                  <h3>{company.name}</h3>
                  <p>{company.address}</p>
                  <p>
                    GSTIN: {company.gstin} | Website: {company.website} | Email: {company.email}
                  </p>
                  <p>Mobile: {company.phone}</p>
                </div>
              </div>
              <div className="ea-docbox">
                <span>Document</span>
                <strong>Employee Agreement</strong>
                <small>Acceptance Time: {fmtDateTime(agreement.acceptedAt)}</small>
              </div>
            </header>

            <section className="ea-intro">
              <h1>Employee Agreement</h1>
              <p>
                This agreement is made between <strong>{company.name}</strong> and{" "}
                <strong>{employee.fullName || "the employee"}</strong>.
              </p>
            </section>

            <section className="ea-grid">
              <article className="ea-summary-card">
                <span>Employee Name</span>
                <strong>{employee.fullName || "-"}</strong>
                <p>{employee.address || "-"}</p>
              </article>
              <article className="ea-summary-card">
                <span>Department / Location</span>
                <strong>{employee.departmentName || "Assigned location"}</strong>
                <p>{employee.email || "Email not available"}</p>
              </article>
            </section>

            <div className="ea-sections">
              {sections.map((section) => (
                <section className="ea-section" key={section.number}>
                  <div className="ea-section-head">
                    <span>{section.number}</span>
                    <h4>{section.title}</h4>
                  </div>
                  {section.body ? <p>{section.body}</p> : null}
                  {section.bullets?.length ? (
                    <ul>
                      {section.bullets.map((bullet) => (
                        <li key={bullet}>{bullet}</li>
                      ))}
                    </ul>
                  ) : null}
                </section>
              ))}
            </div>

            <footer className="ea-signatures">
              <article className="ea-sign-card">
                <span>Employee Signature</span>
                <div className="ea-sign-line">
                  {agreement.signatureDataUrl ? (
                    <img src={agreement.signatureDataUrl} alt="Employee signature" className="ea-sign-image" />
                  ) : (
                    <strong>{agreement.signName || employee.fullName || "Pending signature"}</strong>
                  )}
                </div>
                <div className="ea-sign-meta">
                  <strong>{agreement.signName || employee.fullName || "-"}</strong>
                  <small>{employee.address || "-"}</small>
                  <small>{fmtDateTime(agreement.acceptedAt)}</small>
                </div>
              </article>

              <article className="ea-sign-card company">
                <span>Company Signatory</span>
                <div className="ea-sign-line">
                  <strong>{company.authorizedSignatoryName}</strong>
                </div>
                <div className="ea-sign-meta">
                  <strong>{company.name}</strong>
                  <small>{company.authorizedSignatoryRole}</small>
                  <small>{company.email}</small>
                </div>
              </article>
            </footer>
          </section>
        </Col>

        <Col xl={4}>
          <section className="ea-side-card">
            <h4>Acceptance Status</h4>
            <div className={`ea-status ${isAccepted ? "accepted" : "pending"}`}>
              <span className="material-symbols-outlined">
                {isAccepted ? "verified" : "pending_actions"}
              </span>
              <div>
                <strong>{isAccepted ? "Agreement Accepted" : "Action Required"}</strong>
                <p>
                  {isAccepted
                    ? `Signed on ${fmtDateTime(agreement.acceptedAt)}`
                    : "Employee ko agreement accept karke digital signature save karni hai."}
                </p>
              </div>
            </div>

            <div className="ea-side-points">
              <div>
                <span>Signer</span>
                <strong>{agreement.signName || employee.fullName || "-"}</strong>
              </div>
              <div>
                <span>Version</span>
                <strong>{agreement.version || 1}</strong>
              </div>
              <div>
                <span>Application Link</span>
                <strong>{company.appLink}</strong>
              </div>
            </div>

            <Alert variant={isAccepted ? "success" : "light"} className="ea-side-alert">
              {isAccepted
                ? "Digital signature save ho chuki hai. Ye record employee onboarding proof ke roop me use hoga."
                : "Accept karte hi employee name, signature image, aur date-time agreement record me store ho jayega."}
            </Alert>

            <Button
              className="ea-accept-btn"
              variant={isAccepted ? "outline-success" : "success"}
              disabled={isAccepted || saving}
              onClick={() => {
                setSignatureError("");
                setShowSignModal(true);
              }}
            >
              {isAccepted ? "Already Accepted" : "Accept and Sign"}
            </Button>
          </section>
        </Col>
      </Row>

      <Modal
        show={showSignModal}
        onHide={() => {
          if (saving) return;
          setShowSignModal(false);
          setSignatureError("");
        }}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Digital Signature</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="ea-modal-copy">
            Neeche apna naam confirm kijiye aur signature box me sign draw karke agreement accept kijiye.
          </p>
          <Form.Group className="mb-3">
            <Form.Label>Signer Name</Form.Label>
            <Form.Control
              value={signName}
              onChange={(event) => setSignName(event.target.value)}
              placeholder="Enter signer name"
            />
          </Form.Group>
          <div className="ea-pad-wrap">
            <canvas
              ref={canvasRef}
              className="ea-pad"
              width={SIGN_PAD_WIDTH}
              height={SIGN_PAD_HEIGHT}
              onMouseDown={startSignature}
              onMouseMove={moveSignature}
              onMouseUp={endSignature}
              onMouseLeave={endSignature}
              onTouchStart={startSignature}
              onTouchMove={moveSignature}
              onTouchEnd={endSignature}
            />
          </div>
          {signatureError ? <div className="ea-sign-error">{signatureError}</div> : null}
          <div className="ea-modal-actions">
            <Button variant="outline-secondary" onClick={clearSignature} disabled={saving}>
              Clear
            </Button>
            <Button variant="success" onClick={handleAccept} disabled={saving}>
              {saving ? "Saving..." : "Accept Agreement"}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <ToastContainer position="top-right" autoClose={2500} />
    </div>
  );
};

export default EmployeeAgreement;
